import { currentWeek, isWeekKey } from '@supply/contracts'
import * as React from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'

import { taskByKey } from '@/data/api'
import { isMockActive } from '@/data/mock/mode'
import { lastLocalWriteAt, onLocalWrite, refreshAll } from '@/data/use-load'
import { appEnv } from '@/lib/env'
import { getInitials } from '@/lib/format'
import {
  actorOf,
  ATA_GERAL,
  createLiveRouter,
  LIVE_TABLES,
  openRecordFromLocation,
  touchesOpenRecord,
  viewKeyOf,
  type LiveChange,
  type LiveRouter,
  type OpenRecord,
} from '@/lib/live-router'
import { useSession } from '@/lib/session'
import { getSupabase } from '@/lib/supabase'
import { getBridgeSupabaseAccessToken, renewBridgeSupabaseAccessToken } from '@/lib/supabase-auth-token'
import { isViewingAs } from '@/lib/view-as'

/**
 * Tempo real do Supply — molde do Hub SISTEMAS (ADR-0023 de lá).
 *
 * UM canal por sessão (`supply:ao-vivo`, privado), no Realtime do Supabase:
 *   - Postgres Changes das tabelas de `LIVE_TABLES` (migration
 *     20261022170000). O Realtime entrega a cada pessoa SÓ o que o RLS dela
 *     deixa ler — o mesmo filtro das telas;
 *   - presença: quem está online e o que está vendo (tarefa aberta, ATA).
 *
 * As telas não assinam nada: a mudança vira `refreshAll()` (ou
 * `reloadReference`, para áreas/pessoas/metas) depois de ~300 ms sem novas
 * mudanças, e o `useLoad` recarrega sem piscar, reaproveitando o que não mudou.
 *
 * Rascunho não se perde: enquanto a pessoa digita (campo com texto alterado e
 * em foco) ou tem texto não salvo num painel/diálogo aberto, a recarga espera.
 * Se o registro aberto mudou por outra pessoa, um toast avisa — com
 * "Atualizar" quando a recarga está segurada pelo rascunho.
 *
 * Sem conexão (rede, Realtime fora), nada quebra: as telas seguem lendo ao
 * abrir, ao voltar a aba e nos intervalos que já tinham.
 */

const TOPIC = 'supply:ao-vivo'

// ---------------------------------------------------------------------------
// Mudanças do banco
// ---------------------------------------------------------------------------

type ChangeListener = (change: LiveChange) => void
const changeListeners = new Set<ChangeListener>()

/** Ouve toda mudança que o canal entrega (já filtrada pelo RLS). */
export function onLiveChange(listener: ChangeListener): () => void {
  changeListeners.add(listener)
  return () => {
    changeListeners.delete(listener)
  }
}

/** Exposto para teste: é por aqui que a conexão entrega cada mudança. */
export function emitLiveChange(change: LiveChange): void {
  for (const listener of changeListeners) {
    try {
      listener(change)
    } catch (error) {
      console.warn('[tempo real] ouvinte falhou', error)
    }
  }
}

// ---------------------------------------------------------------------------
// Estado da conexão e presença (lojas externas, fora do React)
// ---------------------------------------------------------------------------

export type LiveStatus = 'desligado' | 'conectando' | 'ao_vivo' | 'caiu'

/** Uma pessoa online (várias abas dela viram uma entrada). */
export interface PresencePerson {
  readonly memberId: string
  readonly name: string
  readonly avatarUrl: string | null
  /** ISO da aba que entrou primeiro. */
  readonly onlineAt: string
  /** O que cada aba dela está vendo (`viewKeyOf`), sem repetição. */
  readonly views: readonly string[]
}

/** O que cada aba anuncia no canal (`track`). */
interface PresenceMeta {
  readonly memberId: string
  readonly name: string
  readonly avatarUrl: string | null
  readonly onlineAt: string
  readonly view: string | null
}

let status: LiveStatus = 'desligado'
let people: readonly PresencePerson[] = []
/** O que esta aba está vendo (`viewKeyOf`): tarefa aberta ou ATA da reunião. */
let myView: string | null = null
const storeListeners = new Set<() => void>()
const notifyStore = () => storeListeners.forEach((notify) => notify())
const subscribeStore = (notify: () => void) => {
  storeListeners.add(notify)
  return () => {
    storeListeners.delete(notify)
  }
}

function setStatus(next: LiveStatus) {
  if (status === next) return
  status = next
  notifyStore()
}

function setPeople(next: readonly PresencePerson[]) {
  people = next
  notifyStore()
}

function setMyView(next: string | null) {
  if (myView === next) return
  myView = next
  notifyStore()
}

/** O que esta aba está vendo agora (`tarefa:SUP-12`, `ata:ras:2026-W43:geral`). */
export function useMyView(): string | null {
  return React.useSyncExternalStore(subscribeStore, () => myView)
}

/** Junta as metas por membro, como o `presenceState()` do Realtime entrega. */
export function peopleFromPresence(state: Record<string, readonly Partial<PresenceMeta>[]>): PresencePerson[] {
  const out: PresencePerson[] = []
  for (const [key, metas] of Object.entries(state)) {
    const valid = metas.filter((m) => typeof m.name === 'string')
    const first = valid[0]
    if (!first) continue
    const onlineAt = valid.map((m) => m.onlineAt ?? '').filter(Boolean).sort()[0] ?? ''
    const views = [...new Set(valid.map((m) => m.view).filter((v): v is string => typeof v === 'string'))]
    out.push({ memberId: first.memberId ?? key, name: first.name ?? '', avatarUrl: first.avatarUrl ?? null, onlineAt, views })
  }
  return out.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
}

export function useLiveStatus(): LiveStatus {
  return React.useSyncExternalStore(subscribeStore, () => status)
}

/** Quem está online agora (inclui eu), uma entrada por pessoa. */
export function usePresence(): { status: LiveStatus; people: readonly PresencePerson[] } {
  const s = React.useSyncExternalStore(subscribeStore, () => status)
  const p = React.useSyncExternalStore(subscribeStore, () => people)
  return { status: s, people: p }
}

/** As OUTRAS pessoas vendo a mesma coisa (`view`) que eu. */
export function useViewers(view: string | null): readonly PresencePerson[] {
  const { member } = useSession()
  const all = React.useSyncExternalStore(subscribeStore, () => people)
  return React.useMemo(
    () => (view ? all.filter((p) => p.memberId !== member.id && p.views.includes(view)) : []),
    [all, view, member.id],
  )
}

// ---------------------------------------------------------------------------
// A conexão: uma para o app, fora do React
// ---------------------------------------------------------------------------
// Montar de novo (StrictMode em dev) reaproveita a que existe: tirar o último
// canal derruba o socket, e o canal novo ficaria pendurado.

interface Connection {
  readonly memberId: string
  setMeta(meta: Omit<PresenceMeta, 'onlineAt'>): void
  close(): void
}

let connection: Connection | null = null
/** Dev admins fantasmas: nunca entram na lista de presença (`session.ghostIds`). */
let hiddenIds: ReadonlySet<string> = new Set()
let teardown: number | null = null

/**
 * `silent`: o dev admin fantasma (ADR 0008) recebe as mudanças, mas não
 * anuncia presença — não aparece nos avatares de ninguém.
 */
function openConnection(initial: Omit<PresenceMeta, 'onlineAt'>, silent: boolean): Connection {
  const sb = getSupabase()
  const onlineAt = new Date().toISOString()
  let meta: PresenceMeta = { ...initial, onlineAt }

  // O token do bridge vai no cabeçalho HTTP (fetch); o socket precisa dele à
  // parte, senão entra como `anon` (o canal privado recusa e o RLS não entrega
  // nada). No login local, `setAuth()` sem argumento lê a sessão do supabase-js.
  // Precisa terminar ANTES de assinar: o join leva o token da hora.
  const applyAuth = () => sb.realtime.setAuth(getBridgeSupabaseAccessToken() ?? null)

  let channel: ReturnType<typeof sb.channel> | null = null
  let retry: number | null = null
  let attempt = 0
  let closed = false
  let wasLive = false

  const connect = async () => {
    if (closed) return
    retry = null
    await applyAuth().catch(() => undefined)
    if (closed) return
    let next = sb.channel(TOPIC, { config: { private: true, presence: { key: meta.memberId } } })
    for (const table of LIVE_TABLES) {
      next = next.on('postgres_changes', { event: '*', schema: 'supply', table }, (payload) =>
        emitLiveChange({
          table,
          eventType: payload.eventType as LiveChange['eventType'],
          row: (payload.new ?? {}) as Record<string, unknown>,
          old: (payload.old ?? {}) as Record<string, unknown>,
        }),
      )
    }
    next = next.on('presence', { event: 'sync' }, () => {
      // Segunda trava: cliente antigo de um admin que ainda anuncie fica de fora.
      if (channel === next) setPeople(peopleFromPresence(next.presenceState<PresenceMeta>()).filter((p) => !hiddenIds.has(p.memberId)))
    })
    channel = next
    setStatus('conectando')
    next.subscribe((state) => {
      if (closed || channel !== next) return
      if (state === 'SUBSCRIBED') {
        attempt = 0
        setStatus('ao_vivo')
        if (!silent) void next.track(meta)
        // Voltou depois de cair: o que mudou no meio não chegou — relê tudo.
        if (wasLive) refreshAll()
        wasLive = true
      } else if (state === 'CHANNEL_ERROR' || state === 'TIMED_OUT' || state === 'CLOSED') {
        setStatus('caiu')
        setPeople([])
        channel = null
        void sb.removeChannel(next)
        // Espera crescente até 30 s. A partir da segunda falha seguida, renova
        // o token do bridge (aba aberta há horas: o socket cai por token vencido).
        const wait = Math.min(30_000, 1_000 * 2 ** attempt)
        attempt += 1
        retry = window.setTimeout(() => {
          void (async () => {
            if (appEnv.authMode !== 'local' && attempt > 1) await renewBridgeSupabaseAccessToken()
            await connect()
          })()
        }, wait)
      }
    }, 20_000)
  }

  // Voltou a rede ou a aba: se caiu, tenta já em vez de esperar a vez.
  const reconnectNow = () => {
    if (closed || channel || retry === null) return
    window.clearTimeout(retry)
    attempt = 0
    void connect()
  }
  const onVisible = () => {
    if (document.visibilityState === 'visible') reconnectNow()
  }
  window.addEventListener('online', reconnectNow)
  document.addEventListener('visibilitychange', onVisible)
  // O token do bridge renova de tempos em tempos: o socket recebe o novo.
  const timer = window.setInterval(() => void applyAuth().catch(() => undefined), 60_000)

  void connect()

  return {
    memberId: initial.memberId,
    setMeta(next) {
      if (next.view === meta.view && next.name === meta.name && next.avatarUrl === meta.avatarUrl) return
      meta = { ...next, onlineAt }
      if (channel && status === 'ao_vivo' && !silent) void channel.track(meta)
    },
    close() {
      closed = true
      window.removeEventListener('online', reconnectNow)
      document.removeEventListener('visibilitychange', onVisible)
      window.clearInterval(timer)
      if (retry !== null) window.clearTimeout(retry)
      if (channel) void sb.removeChannel(channel)
      channel = null
      setPeople([])
      setStatus('desligado')
    },
  }
}

// ---------------------------------------------------------------------------
// Rascunho: quando segurar a recarga
// ---------------------------------------------------------------------------

const TEXT_INPUTS = new Set(['', 'text', 'number', 'email', 'tel', 'url', 'password'])

/** Campo de texto que guarda rascunho (busca e combobox não contam). */
function isDraftField(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false
  if (el.closest('[data-live-ignore]') || el.getAttribute('role') === 'combobox') return false
  if (el instanceof HTMLTextAreaElement) return !el.readOnly
  if (el instanceof HTMLInputElement) return TEXT_INPUTS.has(el.type) && !el.readOnly
  return el.isContentEditable
}

/** Campos com texto alterado e painéis/diálogos (`role=dialog`) com rascunho. */
const dirtyFields = new Set<HTMLElement>()
const dirtyDialogs = new Set<HTMLElement>()

function clearDrafts() {
  dirtyFields.clear()
  dirtyDialogs.clear()
}

function trackInput(event: Event) {
  const target = event.target as Element | null
  if (!isDraftField(target)) return
  dirtyFields.add(target)
  const dialog = target.closest<HTMLElement>('[role="dialog"]')
  if (dialog) dirtyDialogs.add(dialog)
}

/**
 * Segura a recarga enquanto: o campo em foco tem texto alterado, ou um
 * painel/diálogo aberto tem texto ainda não salvo. Escrita própria
 * (`invalidate()`) ou fechar o painel libera.
 */
export function isEditingDraft(): boolean {
  for (const set of [dirtyFields, dirtyDialogs]) {
    for (const el of set) if (!el.isConnected) set.delete(el)
  }
  const active = document.activeElement
  if (active instanceof HTMLElement && dirtyFields.has(active)) return true
  return dirtyDialogs.size > 0
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/** Uma mudança sem autor na linha é "minha" se escrevi há menos disso. */
const OWN_WRITE_WINDOW_MS = 4_000

/**
 * Abre a conexão (a sessão já está resolvida aqui dentro), roteia as mudanças
 * para recarga e mantém a presença com o que a pessoa está vendo.
 */
export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const { member, me, memberById, reloadReference, ghostIds } = useSession()
  const location = useLocation()
  const silent = ghostIds.has(member.id)
  React.useEffect(() => {
    hiddenIds = ghostIds
  }, [ghostIds])

  // Conexão — fecha no logout (o provider sai junto com a sessão).
  React.useEffect(() => {
    // "Ver como": o Realtime avalia pelo JWT do gestor e a presença anunciaria
    // o gestor com o nome da pessoa. No modo, sem canal (ADR 0007).
    if (isViewingAs()) return
    // Dados fictícios (SIS-465): sem canal — nada de presença real nem mudanças do banco real.
    if (isMockActive()) return
    if (teardown !== null) {
      window.clearTimeout(teardown)
      teardown = null
    }
    if (connection && connection.memberId !== member.id) {
      connection.close()
      connection = null
    }
    connection ??= openConnection({ memberId: member.id, name: member.full_name, avatarUrl: member.avatar_url, view: null }, silent)
    return () => {
      teardown = window.setTimeout(() => {
        connection?.close()
        connection = null
        teardown = null
      }, 1_000)
    }
  }, [member.id, member.full_name, member.avatar_url, silent])

  // Registro aberto (tarefa do painel ou ATA da reunião), lido da URL.
  const defaultAreaKey = me.areas.length === 1 ? (me.areas[0]?.areaId ?? ATA_GERAL) : ATA_GERAL
  const parsed = React.useMemo(
    () => openRecordFromLocation(location.pathname, location.search, { week: currentWeek(), areaKey: defaultAreaKey, isWeek: isWeekKey }),
    [location.pathname, location.search, defaultAreaKey],
  )
  const view = viewKeyOf(parsed)
  const [taskId, setTaskId] = React.useState<{ key: string; id: string | null } | null>(null)
  const taskKey = parsed?.kind === 'tarefa' ? parsed.key : null
  React.useEffect(() => {
    if (!taskKey) return
    let cancelled = false
    taskByKey(taskKey)
      .then((t) => !cancelled && setTaskId({ key: taskKey, id: t?.id ?? null }))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [taskKey])
  const open: OpenRecord | null =
    parsed?.kind === 'tarefa' ? { ...parsed, id: taskId?.key === parsed.key ? taskId.id : null } : parsed

  React.useEffect(() => {
    setMyView(view)
    connection?.setMeta({ memberId: member.id, name: member.full_name, avatarUrl: member.avatar_url, view })
  }, [member.id, member.full_name, member.avatar_url, view])

  // O roteador vive o tempo da sessão; o que muda (registro aberto, nomes)
  // chega por ref.
  const live = React.useRef({ open, memberId: member.id, memberById, reloadReference })
  live.current = { open, memberId: member.id, memberById, reloadReference }

  React.useEffect(() => {
    const router: LiveRouter = createLiveRouter({
      delayMs: 300,
      hold: isEditingDraft,
      flush: (scope) => {
        if (scope === 'referencia') void live.current.reloadReference({ remote: true })
        else refreshAll()
      },
    })

    // Registro aberto mudou por outra pessoa: avisa (uma vez por rajada).
    let noticeTimer: number | null = null
    let noticeActor: string | null = null
    const noticeOpenRecord = (change: LiveChange) => {
      const { open: current, memberId } = live.current
      if (!touchesOpenRecord(change, current)) return
      const actor = actorOf(change)
      if (actor === memberId) return
      noticeActor = actor ?? noticeActor
      if (noticeTimer !== null) window.clearTimeout(noticeTimer)
      noticeTimer = window.setTimeout(() => {
        noticeTimer = null
        const who = noticeActor
        noticeActor = null
        if (!who && Date.now() - lastLocalWriteAt() < OWN_WRITE_WINDOW_MS) return
        const name = who ? live.current.memberById.get(who)?.full_name.split(' ')[0] : undefined
        const what = live.current.open?.kind === 'ata' ? 'esta ATA' : 'esta tarefa'
        const held = isEditingDraft()
        toast.info('Atualizado por outra pessoa', {
          id: 'ao-vivo-registro-aberto',
          description: `${name ?? 'Alguém'} mudou ${what}.${held ? ' Seu rascunho foi mantido.' : ''}`,
          // Com rascunho, o aviso tem ação: fica tempo de a pessoa decidir.
          duration: held ? 15_000 : 4_000,
          action: held
            ? {
                label: 'Atualizar',
                onClick: () => {
                  clearDrafts()
                  router.force()
                },
              }
            : undefined,
        })
      }, 400)
    }

    const offChange = onLiveChange((change) => {
      router.push(change)
      noticeOpenRecord(change)
    })
    const offWrite = onLocalWrite(() => {
      clearDrafts()
      router.kick()
    })
    // Saiu do campo: se nada mais segura, a recarga esperando vai.
    const onFocusOut = () => window.setTimeout(() => router.kick(), 150)
    document.addEventListener('input', trackInput, true)
    document.addEventListener('focusout', onFocusOut, true)
    return () => {
      offChange()
      offWrite()
      document.removeEventListener('input', trackInput, true)
      document.removeEventListener('focusout', onFocusOut, true)
      if (noticeTimer !== null) window.clearTimeout(noticeTimer)
      router.dispose()
    }
  }, [])

  return (
    <>
      {children}
      {open?.kind === 'tarefa' && <TaskViewers view={view} />}
    </>
  )
}

/**
 * "Erick está vendo esta tarefa": fica sobre o painel da tarefa, acima do
 * rodapé de ações (o topo da tela, onde mora o indicador, está coberto por ele).
 */
function TaskViewers({ view }: { view: string | null }) {
  const viewers = useViewers(view)
  if (viewers.length === 0) return null
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-16 right-4 z-[60] flex items-center gap-2 rounded-full border border-border bg-popover py-1 pl-1 pr-3 text-[12px] text-popover-foreground shadow-md"
    >
      <ViewerAvatars viewers={viewers} />
      <span>{viewersLabel(viewers, 'esta tarefa')}</span>
    </div>
  )
}

export function viewersLabel(viewers: readonly PresencePerson[], what: string): string {
  const first = (p: PresencePerson) => p.name.split(' ')[0] ?? p.name
  const [a, b] = viewers
  if (!a) return ''
  if (viewers.length === 1) return `${first(a)} está vendo ${what}`
  if (viewers.length === 2 && b) return `${first(a)} e ${first(b)} estão vendo ${what}`
  return `${first(a)} e mais ${viewers.length - 1} estão vendo ${what}`
}

export function ViewerAvatars({ viewers, max = 3 }: { viewers: readonly PresencePerson[]; max?: number }) {
  return (
    <span className="flex -space-x-1.5" aria-hidden>
      {viewers.slice(0, max).map((p) => (
        <span
          key={p.memberId}
          className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-popover bg-primary text-[10px] font-semibold text-primary-foreground"
        >
          {getInitials(p.name)}
        </span>
      ))}
    </span>
  )
}
